import {
  type ListOutputEntriesParams,
  buildOutputBatch,
  buildOutputEntry,
  changeOutputConfig,
  fetchDeliveryOutputConfig,
  fetchOutputEntryTask,
  fetchOutputReadLease,
  fetchOutputSummary,
  finalizeDelivery,
  listCustomerOutputOrphans,
  listOutputEntries,
  patchOutputEntryMetadata,
  publishOutputManifests,
  purgeCustomerOutputOrphans,
  rerunOutputEntry,
  resolveOutputEntryMetadata,
  resolveOutputMetadata,
} from '@/features/delivery/api/outputs';
import {
  DELIVERY_QUERY_KEY,
  OUTPUT_ENTRIES_PAGE_SIZE,
  OUTPUT_ORPHANS_PAGE_SIZE,
} from '@/features/delivery/config/constants';
import { activeBuildPollInterval } from '@/features/delivery/lib/buildPolling';
import {
  toOutputEntryMetadataResult,
  toOutputEntryPage,
} from '@/features/delivery/lib/metadataSnapshot';
import { isActiveTaskState } from '@/features/delivery/types/contracts';
import type {
  BuildScope,
  ChangeOutputConfigInput,
  MetadataStatus,
  OutputBuildState,
  OutputEntryPage,
  OutputEntryPageResponse,
  OutputManifestMode,
  PatchOutputEntryMetadataInput,
  PurgeOrphansInput,
  RerunOutputEntryInput,
} from '@/features/delivery/types/outputContracts';
import { ApiHttpError } from '@/shared/api/client';
import {
  type InfiniteData,
  type QueryClient,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect } from 'react';
import {
  invalidateDeliveryAggregate,
  invalidateDeliveryBuildReads,
} from './deliveryQueryInvalidation';

export const outputConfigKey = (deliveryId: string) =>
  [DELIVERY_QUERY_KEY, 'output-config', deliveryId] as const;
const outputSummaryKey = (deliveryId: string) =>
  [DELIVERY_QUERY_KEY, 'output-summary', deliveryId] as const;
// The scope-carrying segments come after the output id, so the prefix-based
// invalidation below covers every loaded term, batch and state narrowing at once.
// `null` rather than `undefined` for "every batch"/"every state": `undefined` is
// dropped from a serialized key, which would make the unnarrowed listing's key a
// PREFIX of a narrowed one instead of a sibling.
const outputEntriesKey = (
  deliveryId: string,
  term: string,
  batchNo?: number,
  state?: OutputBuildState,
  metadataStatus?: MetadataStatus,
) =>
  [
    DELIVERY_QUERY_KEY,
    'output-entries',
    deliveryId,
    batchNo ?? null,
    state ?? null,
    metadataStatus ?? null,
    term,
  ] as const;
const outputEntryTaskKey = (deliveryId: string, entryId: string) =>
  [DELIVERY_QUERY_KEY, 'output-entry-task', deliveryId, entryId] as const;
// `null` for "every batch" for the same reason as the entries key: the audit's cursor is
// scope-bound, so the narrowed and unnarrowed audits must be cache siblings.
const outputOrphansKey = (deliveryId: string, batchNo?: number) =>
  [DELIVERY_QUERY_KEY, 'output-orphans', deliveryId, batchNo ?? null] as const;

async function nullOnNotFound<T>(request: () => Promise<T>): Promise<T | null> {
  try {
    return await request();
  } catch (error) {
    if (error instanceof ApiHttpError && error.status === 404) return null;
    throw error;
  }
}

/** Queued and running Output tasks both persist the Entry as building. */
function isActiveState(state: OutputBuildState): boolean {
  return state === 'building';
}

/** Refresh a output's summary and every loaded entries scope after a build. */
export function invalidateOutput(qc: QueryClient, deliveryId: string) {
  return Promise.all([
    invalidateDeliveryBuildReads(qc, deliveryId),
    qc.invalidateQueries({ queryKey: outputSummaryKey(deliveryId) }),
    // The prefix stops at the output id, so this covers the unnarrowed listing and
    // every `(batchNo, term)` variant of it.
    qc.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY, 'output-entries', deliveryId] }),
    // Any per-entry task view for this output is stale after a build enqueues.
    qc.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY, 'output-entry-task', deliveryId] }),
    // And so is the orphan audit: a build writes new objects and supersedes old ones, so
    // every loaded disposition was computed against storage as it no longer is. This is
    // also what refreshes the audit after a purge.
    qc.invalidateQueries({ queryKey: [DELIVERY_QUERY_KEY, 'output-orphans', deliveryId] }),
  ]);
}

// --- Reads ---

export function useDeliveryOutputConfig(deliveryId: string) {
  return useQuery({
    queryKey: outputConfigKey(deliveryId),
    queryFn: ({ signal }) => fetchDeliveryOutputConfig(deliveryId, { signal }),
    enabled: !!deliveryId,
    refetchInterval: (query) =>
      activeBuildPollInterval(query, Boolean(query.state.data?.finalizationTaskId)),
  });
}

/**
 * The output's rollup, polled while a build is running (`refetchInterval`).
 *
 * Mounted by every surface that states output status — `OutputStatusLine` and
 * `OutputActionsMenu` state the WHOLE output (no `batchNo`) and share one request;
 * `DeliveryEntriesWorkspace` additionally reads the filtered batch's own rollup to gate
 * the batch build actions.
 *
 * `batchNo` is in the query key, so the two scopes are cache SIBLINGS rather than one
 * entry the narrowed and unnarrowed reads overwrite in turn. `outputSummaryKey` stays the
 * prefix, so `invalidateOutput` still refreshes every loaded scope in one call.
 */
export function useOutputSummary(deliveryId: string, batchNo?: number) {
  const qc = useQueryClient();
  const result = useQuery({
    // `'all'` rather than `undefined`: an undefined tail is dropped from a serialized
    // key, which would make the whole-output key a PREFIX of a narrowed one.
    queryKey: [...outputSummaryKey(deliveryId), batchNo ?? 'all'],
    queryFn: ({ signal }) => fetchOutputSummary(deliveryId, { batchNo }, { signal }),
    enabled: !!deliveryId,
    refetchInterval: (query) =>
      activeBuildPollInterval(
        query,
        Boolean(query.state.data && query.state.data.entries.building > 0),
      ),
  });
  const counts = result.data?.entries;
  // The whole-output rollup is mounted even on the Source facet. Refresh its shared Entry reads
  // when worker reconciliation changes counts, including the final response that stops polling.
  // Stable counts avoid repeating the Delivery aggregate query on every unchanged polling tick.
  useEffect(() => {
    if (batchNo === undefined && counts) void invalidateDeliveryBuildReads(qc, deliveryId);
  }, [qc, deliveryId, batchNo, counts]);
  return result;
}

/** Whether an entry the client has actually loaded is still building. */
function entriesAreActive(pages: { items: { state: OutputBuildState }[] }[] | undefined) {
  const entries = pages?.flatMap((page) => page.items) ?? [];
  return entries.some((entry) => isActiveState(entry.state));
}

function selectOutputEntries(
  data: InfiniteData<OutputEntryPageResponse, string | undefined>,
): InfiniteData<OutputEntryPage, string | undefined> {
  return { ...data, pages: data.pages.map(toOutputEntryPage) };
}

/** Which slice of a output's entries a caller wants. Both narrow server-side. */
export type OutputEntriesFilters = Pick<
  ListOutputEntriesParams,
  'batchNo' | 'state' | 'metadataStatus'
>;

/**
 * A output's entries as a keyset-paginated query — every batch, or one
 * batch when `batchNo` is given; every build state, or one when `state` is given. No
 * `requested` gate, unlike {@link useOutputEntryTask}: the callers are the `output`
 * facet's table and the cross-batch search dialog, each mounted only while it is
 * shown, so mounting IS the gate.
 *
 * ONE hook for every scope, because on the wire they are one listing under optional
 * `?batchNo=`/`?state=` (see `listOutputEntries`) — which is what lets the workspace
 * switch a filter without switching hooks. Both narrowings are part of the query key,
 * so each scope caches its own pages and its own cursors (a cursor encodes where the
 * *filtered* scan stopped, so it never crosses scopes).
 *
 * `state` is the PACKAGE build state (`pending`/`blocked`/`building`/`ready`/`failed`),
 * a disjoint vocabulary from the delivery entry's `active`/`voided` — the backend 400s
 * a foreign value rather than widening, so a value must never be carried across the two
 * facets.
 *
 * `q` is the operator's free-text filter, applied by the backend over the readable keys
 * and the source filename. It is trimmed and folded into the key too, so each settled
 * term caches its own paginated result; a blank term is the unfiltered listing and is
 * omitted from the request entirely. `keepPreviousData` keeps the previous scope's rows
 * on screen (dimmed via `stale`) while the new one's first page loads, instead of
 * flashing a skeleton.
 *
 * Every filter is fail-closed server-side (an invalid one is a 400), so nothing here
 * may retry a rejected request with the parameter dropped — that would answer a
 * narrowed question with the unnarrowed listing.
 *
 * `staleWhileFetching: false` because this list polls while any loaded entry is still
 * building — dimming every row on each tick would read as a broken table rather than
 * as staleness. `nextCursor` is opaque and echoed back verbatim.
 */
export function useOutputEntries(
  deliveryId: string,
  q = '',
  { batchNo, state, metadataStatus }: OutputEntriesFilters = {},
) {
  const term = q.trim();
  return useInfiniteQuery({
    queryKey: outputEntriesKey(deliveryId, term, batchNo, state, metadataStatus),
    queryFn: ({ pageParam, signal }) =>
      listOutputEntries(
        deliveryId,
        {
          limit: OUTPUT_ENTRIES_PAGE_SIZE,
          cursor: pageParam,
          ...(batchNo === undefined ? {} : { batchNo }),
          ...(state ? { state } : {}),
          ...(metadataStatus ? { metadataStatus } : {}),
          ...(term ? { q: term } : {}),
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!deliveryId,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      activeBuildPollInterval(query, entriesAreActive(query.state.data?.pages)),
    select: selectOutputEntries,
  });
}

/**
 * A output's candidate ORPHANED OUTPUTS as a paged query, plus the audit's
 * own `complete` flag. Admin-only on the backend (`.manage`); mounted by a dialog, so
 * mounting is the gate.
 *
 * Two things here are NOT the usual infinite-list wiring, both forced by this
 * endpoint's contract:
 *
 * 1. **A page may be empty while more candidates remain.** The cursor is generated over
 *    the SQL candidate rows and the backend then drops the candidates its pure re-check
 *    rejects, so `items: [], nextCursor: '…'` is normal. A normal range controller would render
 *    the empty state without paging — which would
 *    stop the audit dead at the first such page and report "no leaks" for a output that
 *    has them. So while rows are still absent and a cursor remains, this hook DRIVES the
 *    paging itself and reports `pending`: it is still looking, not finished. The hook continues
 *    draining after rows land too, because the audit must be complete before it can claim that
 *    no orphan exists. The caller keeps already-loaded rows visible while remaining pages load.
 * 2. **`complete` is per page and must be AND-ed.** One unprobeable object anywhere in
 *    what has been loaded means the loaded set is not a complete leak list, so
 *    `complete` here is "every loaded page was complete" — never the last page's flag.
 *
 * The cursor is opaque and scope-bound, so `batchNo` joins the query key (a cursor
 * earned under one batch means nothing under another).
 */
export function useCustomerOutputOrphans(deliveryId: string, batchNo?: number) {
  const infiniteQuery = useInfiniteQuery({
    queryKey: outputOrphansKey(deliveryId, batchNo),
    queryFn: ({ pageParam, signal }) =>
      listCustomerOutputOrphans(
        deliveryId,
        {
          limit: OUTPUT_ORPHANS_PAGE_SIZE,
          cursor: pageParam,
          ...(batchNo === undefined ? {} : { batchNo }),
        },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    enabled: !!deliveryId,
  });
  const pages = infiniteQuery.data?.pages ?? [];
  // AND over the loaded pages, not the newest one: an earlier page's unprobeable object
  // is still unprobed no matter how clean a later page came back.
  const complete = pages.every((page) => page.complete);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = infiniteQuery;
  // This audit is an aggregation query rather than a user-driven range list: every cursor must
  // be drained, including non-empty pages. A failed next-page request explicitly turns this off
  // so the effect cannot immediately retry the same cursor forever.
  const exhausting = Boolean(hasNextPage && !infiniteQuery.isFetchNextPageError);
  // `pages.length` is in the deps for a reason, and the loop stalls without it: after a
  // second consecutive empty page `exhausting`/`isFetchingNextPage` are back to the exact
  // values they had before the fetch (the in-flight render is batched away), so the effect
  // would not re-run and the audit would sit at `pending` forever with candidates unread.
  // The page count is the one value that always advances.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pages.length is the loop's only advancing value
  useEffect(() => {
    if (exhausting && !isFetchingNextPage) void fetchNextPage();
  }, [exhausting, isFetchingNextPage, fetchNextPage, pages.length]);

  return {
    ...infiniteQuery,
    complete,
    isExhausting: exhausting,
  };
}

/**
 * Delete the selected leaked objects (admin-only, `.manage`). Partial success is normal
 * and the per-entry outcomes are the point, so the caller renders the returned report
 * rather than treating a resolved promise as "all deleted" (see `lib/orphanAudit.ts`).
 *
 * Invalidates through {@link invalidateOutput}, which covers the audit itself — after a
 * purge every loaded orphan page is stale by construction.
 */
export function usePurgeCustomerOutputOrphans(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PurgeOrphansInput) => purgeCustomerOutputOrphans(deliveryId, input),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

/**
 * Read a output entry's build task on demand — disabled until `requested` (e.g.
 * the task dialog is open). A `null` result (backend 404) is the normal "output
 * not found / entry not a member / no build task for this generation yet" state,
 * not an error. Polls while the task is still active (pending/running) so a
 * running build's progress and eventual failure surface without a manual
 * refresh; a settled task stops polling.
 */
export function useOutputEntryTask(deliveryId: string, entryId: string, requested: boolean) {
  return useQuery({
    queryKey: outputEntryTaskKey(deliveryId, entryId),
    queryFn: ({ signal }) =>
      nullOnNotFound(() => fetchOutputEntryTask(deliveryId, entryId, { signal })),
    enabled: requested && !!deliveryId && !!entryId,
    refetchInterval: (query) =>
      activeBuildPollInterval(
        query,
        Boolean(query.state.data && isActiveTaskState(query.state.data.state)),
      ),
  });
}

// --- Mutations ---

/**
 * Fetch a short-lived signed read-lease for a ready output entry output on an
 * explicit download click. Modeled as a mutation (imperative, not cached) so
 * the signed URL is never persisted in the query cache; the caller opens the
 * returned `url` and discards it.
 */
export function useDownloadOutputEntry(deliveryId: string) {
  return useMutation({
    mutationFn: (entryId: string) => fetchOutputReadLease(deliveryId, entryId),
  });
}

export function useChangeOutputConfig(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ChangeOutputConfigInput) => changeOutputConfig(deliveryId, input),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: outputConfigKey(deliveryId) }),
        invalidateDeliveryAggregate(qc, deliveryId),
        invalidateOutput(qc, deliveryId),
      ]),
  });
}

/**
 * Advance membership through bounded backend source pages. The backend owns the durable source
 * watermark, so normal client retries neither rescan an already-committed prefix nor need browser
 * persistence. A separate explicit reconcile mode remains available to callers that need to rescan
 * historical sources whose outputs completed after an earlier pass.
 */
/**
 * Preview or apply customer Batch manifests. This stays imperative: manifests
 * are create-only delivery artifacts, not query state to cache or poll.
 */
export function usePublishOutputManifests(deliveryId: string) {
  return useMutation({
    mutationFn: (mode: OutputManifestMode) => publishOutputManifests(deliveryId, mode),
  });
}

/** The same three scopes, narrowed to one derived batch. */
export function useBuildOutputBatch(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { batchNo: number; scope?: BuildScope; reason?: string }) =>
      buildOutputBatch(deliveryId, vars.batchNo, { scope: vars.scope, reason: vars.reason }),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

export function useBuildOutputEntry(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => buildOutputEntry(deliveryId, entryId),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

/**
 * Re-run ONE output entry — `scope: 'failed'` (was Retry) or `'terminal'` (was
 * Rebuild in place, e.g. to pick up a refreshed metadata snapshot). Fenced by the
 * `expectedBuildGeneration` the caller read off the row.
 */
export function useRerunOutputEntry(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { entryId: string; input: RerunOutputEntryInput }) =>
      rerunOutputEntry(deliveryId, vars.entryId, vars.input),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

/** Re-resolve an entry's metadata snapshot from the delivery binding (clears `outdated`). */
export function useResolveOutputEntryMetadata(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) =>
      toOutputEntryMetadataResult(await resolveOutputEntryMetadata(deliveryId, entryId)),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

/** Re-resolve every entry's metadata snapshot in a output (clears output-wide drift). */
export function useResolveOutputMetadata(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => resolveOutputMetadata(deliveryId),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

/** Merge customer metadata overrides into an entry's snapshot. */
export function usePatchOutputEntryMetadata(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { entryId: string; values: PatchOutputEntryMetadataInput }) =>
      toOutputEntryMetadataResult(
        await patchOutputEntryMetadata(deliveryId, vars.entryId, vars.values),
      ),
    onSuccess: () => invalidateOutput(qc, deliveryId),
  });
}

export function useFinalizeDelivery(deliveryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => finalizeDelivery(deliveryId),
    onSuccess: () =>
      Promise.all([
        invalidateOutput(qc, deliveryId),
        qc.invalidateQueries({ queryKey: outputConfigKey(deliveryId) }),
      ]),
  });
}
